import React, { useState, FC } from 'react';
import cn from 'classnames';
import Link from '@base/lib/ThemeLink';
import Tracker from '@base/lib/Tracker';
import { getFeatureMoreTrackData } from 'base/utils/features';
import Countdown, { RenderParamType } from '@base/lib/Countdown';
import { useTrans } from 'base/utils/i18n';
import { Iconfont } from '@base/lib/Iconfont'

export type ModuleTitleProps = React.HTMLAttributes<HTMLDivElement> & {
    text?: string;
    link?: string;
    linkText?: string;
    moduleCode?: string;
    endTimeStamp?: number;
    countdownType?: 'simple' | 'DHMS';
    placement?: 'left' | 'center';
    mergeMore?: boolean; //倒计时和 More 同时生效时，展示倒计时+箭头
}

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

type ICountDown = {
    endTimeStamp: number;
    countdownType: string;
    onEnd: () => void;
    showArrow: boolean;
}
const CountDown: FC<ICountDown> = (props) => {
    const { t } = useTrans();

    const {
        endTimeStamp,
        countdownType,
        onEnd,
        showArrow = false
    } = props

    return (
        <Countdown
            className="countdown g-countdown"
            endTime={endTimeStamp}
            isShowDay={true}
            label={<div className="countdown-label">{t('web_time_ends_in')}&nbsp;</div>}
            arrow={showArrow ? <>&nbsp;<Iconfont className={'icon-arrow-right'}/></> : false}
            onEnd={() => onEnd?.()}
            render={(o: RenderParamType) => {
                if (countdownType === 'simple') {
                    return (Number(o.day) > 0 ? `${o.day}:` : '') + `${o.hour}:${o.minute}:${o.second}`
                }
                let arr: string[] = [];
                if (Number(o.day) > 0) {
                    arr.push(`${o.day}`);
                }
                arr.push(`${o.hour}`);
                arr.push(`${o.minute}`);
                arr.push(`${o.second}`);

                return arr.map(item => item).join(':')
            }}
        />
    )
}

type IMoreLink = {
    linkText: string
}
const MoreLink: FC<IMoreLink> = (props) => {
    const { linkText } = props
    return (
        <div className="g-more more">
            {linkText} <Iconfont className={'icon-arrow-right'}/>
        </div>
    )
}

const ModuleTitleChild = React.forwardRef<HTMLDivElement, ModuleTitleProps>((props, ref) => {
    const {
        text = '',
        link = '',
        linkText = '',
        moduleCode,
        endTimeStamp,
        onClick,
        placement = 'center',
        mergeMore = false,
        countdownType = 'DHMS',
        className,
        ...rest
    } = props;


    const [active, setActive] = useState<boolean>(true);
    const showMoreText = link && linkText;

    const onEnd = () => {
        setActive(false)
    }

    return (
        <div className={cn(className, 'g-title-wrap')} {...rest}>
            <div className={'g-title'}>
                <h2 className={cn('title-text', `text-${placement}`)}>{text}</h2>

                {!isPhone && (<>
                    {/* pc */}
                    {(!!endTimeStamp && active) && (
                        <CountDown
                            countdownType={countdownType}
                            onEnd={onEnd}
                            endTimeStamp={endTimeStamp}
                            showArrow={!!link && (!showMoreText && mergeMore)}
                        />
                    )}
                </>)}
            </div>

            {!isPhone ? (<>
                {/* pc */}
                {showMoreText && !(!!endTimeStamp && active && mergeMore) && (
                    <MoreLink linkText={linkText}/>
                )}
            </>) : (<>
                {/* m */}
                {(!!endTimeStamp && active) ? (
                    <CountDown
                        countdownType={countdownType}
                        onEnd={onEnd}
                        endTimeStamp={endTimeStamp}
                        showArrow={!!link}
                    />
                ) : (<>
                    {(isPhone && showMoreText) && !(!!endTimeStamp && active && mergeMore) && (
                        <MoreLink linkText={linkText}/>
                    )}
                </>)}
            </>)}
        </div>
    )
});

const ModuleTitle: React.FC<ModuleTitleProps> = props => {
    const { text, link = '', moduleCode } = props;
    if (!text) {
        return null;
    }

    return link ? (
        <Link href={link}>
            <a>
                <Tracker data={moduleCode ? getFeatureMoreTrackData(moduleCode) : void 0}>
                    <ModuleTitleChild {...props}/>
                </Tracker>
            </a>
        </Link>
    ) : <ModuleTitleChild {...props}/>
};

export default React.memo(ModuleTitle);